import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export type Promo = { key: string; pct: number; startsOn: string; endsOn: string }

const HOUR = 3_600_000

// Local midnight bounds: the sale runs from the start of `startsOn` to the end
// of `endsOn` on the user's own clock.
export function promoBounds(p: Promo): { start: number; end: number } {
  const start = new Date(`${p.startsOn}T00:00:00`).getTime()
  const last = new Date(`${p.endsOn}T00:00:00`)
  const end = new Date(last.getFullYear(), last.getMonth(), last.getDate() + 1).getTime()
  return { start, end }
}

export function isPromoLive(p: Promo | null | undefined, now = Date.now()): p is Promo {
  if (!p) return false
  const { start, end } = promoBounds(p)
  return now >= start && now < end
}

const pad = (n: number) => String(n).padStart(2, '0')

// Calm "Ends in 5 days" until the last 48 hours, then a live hh:mm:ss.
export function usePromoCountdown(p: Promo | null | undefined) {
  const { t } = useTranslation()
  const [now, setNow] = useState(Date.now)
  const end = p ? promoBounds(p).end : 0
  const left = end - now
  const ticking = left > 0 && left <= 48 * HOUR
  useEffect(() => {
    if (!p) return
    const id = setInterval(() => setNow(Date.now()), ticking ? 1000 : 60_000)
    return () => clearInterval(id)
  }, [p, ticking])

  const live = isPromoLive(p, now)
  if (!live) return { live: false, lastDay: false, label: '' }
  const lastDay = new Date(now).toDateString() === new Date(end - 1).toDateString()
  const label = ticking
    ? t('promo.endsIn', {
        time: `${pad(Math.floor(left / HOUR))}:${pad(Math.floor(left / 60_000) % 60)}:${pad(Math.floor(left / 1000) % 60)}`,
      })
    : t('promo.endsInDays', { count: Math.ceil(left / (24 * HOUR)) })
  return { live: true, lastDay, label }
}
